import { useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Reusable image upload. Pass churchId to scope the upload to a church's bucket.
// onChange receives the public URL ("/api/files/...") that the backend returned.
export default function FileUpload({ churchId, value, onChange, label, accept = "image/*", testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const apiBase = API.replace(/\/api$/, "");
  const previewUrl = value && value.startsWith("/api/") ? `${apiBase}${value}` : value;

  const upload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Max file size is 10MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const endpoint = churchId
        ? `/church-builder/churches/${churchId}/upload`
        : `/admin/upload`;
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div data-testid={testid}>
      {label && <label className="text-[10px] tracking-widest uppercase text-neutral-400 block mb-1">{label}</label>}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files?.[0]); }}
        className={`relative rounded-md border-2 border-dashed transition-colors ${dragOver ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/15"} bg-black/30`}
      >
        {previewUrl ? (
          <div className="relative">
            <img src={previewUrl} alt="" className="w-full max-h-48 object-contain rounded-md" />
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-2 right-2 bg-black/70 text-white rounded-full p-1 hover:bg-red-600"
              aria-label="Remove image"
              data-testid={testid ? `${testid}-remove` : undefined}
            ><X size={14} /></button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full px-4 py-8 flex flex-col items-center justify-center gap-2 text-neutral-400 hover:text-[#D4AF37]"
          >
            {uploading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
            <div className="text-xs tracking-widest uppercase">{uploading ? "Uploading…" : "Choose file or drag & drop"}</div>
            <div className="text-[10px] text-neutral-500">PNG / JPG / WEBP up to 10MB</div>
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          data-testid={testid ? `${testid}-input` : undefined}
          onChange={(e) => upload(e.target.files?.[0])}
        />
        {previewUrl && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="absolute bottom-2 left-2 bg-black/70 text-white text-[10px] tracking-widest uppercase px-2 py-1 rounded hover:bg-black"
            data-testid={testid ? `${testid}-change` : undefined}
          >Replace</button>
        )}
      </div>
    </div>
  );
}
